//@flow
import React, { memo } from "react";
import { View, Platform } from "react-native";
import { NavigationContainer } from "@react-navigation/native";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { getIsLoggedIn } from "@microdoing/shared/services/user/selectors";
import analytics from "@react-native-firebase/analytics";

import { createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import { createMaterialTopTabNavigator } from "@react-navigation/material-top-tabs";

import { useDispatch, useSelector } from "react-redux";
import { setRoute } from "@microdoing/shared/services/nav/actions";
import { navigationRef } from "../../../shared/services/navigation/navigatorHelper";
// 1.0.0-beta.27
import ForgotPasswordScreen from "../scenes/LoginScreen/ForgotPasswordScreen";
import Library from "../scenes/Library/index";

import UserProfile from "../scenes/UserProfile";
import ExternalProfile from "../scenes/UserProfile/ExternalProfile";
import PreferenceSignup from "../scenes/Signup/PreferenceSignup";
import SignupTopics from "../scenes/Signup/SignupTopics";
import SignupSliders from "../scenes/Signup/SignupSliders";
import NotificationScreen from "../scenes/Notification";

import TabBar from "./TabBar/TabBar";
import DesktopTabBar from "./TabBar/DesktopTabBar.web";
import FeelingEvolution from "../scenes/FeelingEvolution";
import RankingScreen from "../scenes/RankingScreen";
import UserRankingScreen from "../scenes/RankingScreen/UserRankingScreen";
import NextActionScreen from "../scenes/NextActionScreen";
import ActionScreen from "../scenes/ActionScreen/ActionScreen";
import MessageScreen from "../scenes/MessageScreen/Conversations";
import DirectoryScreen from "../scenes/SearchScreen/Directory";
import ChatScreen from "../scenes/MessageScreen/ChatScreen/ChatScreen";
import AlgoliaSearch from "../scenes/SearchScreen/AlgoliaSearch";
import HistoricalScreen from "../scenes/ActionScreen/HistoricalActionScreen";
import RandomCoffeeScreen from "../scenes/RandomCoffeeScreen";
import ResultScreen from "../scenes/ResultScreen";
import PollScreen from "../scenes/PollScreen";
import IntroScreen from "../scenes/IntroScreen";
import DailyFocusScreen from "../scenes/CurrentTrackScreen/DailyFocusScreen/DailyFocusScreen";
import ProgressScreen from "../scenes/CurrentTrackScreen/ProgressScreen/ProgressScreen";
import SeeTrackScreen from "../scenes/SeeTrackScreen";
import LibraryFeedback from "../scenes/LibraryFeedback";
import LegalScreen from "../scenes/LegalScreen/index";
import Coaching from "../scenes/Coaching";
import i18n from "@microdoing/shared/i18n";
import NewCampaignModal from "../scenes/CurrentTrackScreen/NewCampaignModal";
import TopTabBar from "./TopTabBar";
import SelectActionScreen from "../scenes/CurrentTrackScreen/DailyFocusScreen/SelectAction/SelectActionScreen";
import SignupMBTI from "../scenes/Signup/SignupMBTI";
import SetRemindersScreen from "../scenes/SetRemindersScreen";
import CampaignResources from "../scenes/Library/CampaignResources";
import CoachResources from "../scenes/Library/CoachResources";
import TeamDetailsScreen from "../scenes/TeamDetailsScreen/TeamDetailsScreen";
import AllowNotificationsScreen from "../scenes/Notification/AllowNotificationsScreen";
import SignupPhoneNumber from "app/scenes/Signup/SignupPhoneNumber";
import WeeklyGoalScreen from "app/scenes/WeeklyGoalScreen";
import MbtiSelectorScreen from "app/scenes/UserProfile/MbtiSelectorScreen/MbtiSelectorScreen";
import DiscSelectorScreen from "app/scenes/UserProfile/Disc/DiscSelectorScreen";
import CancelActionScreen from "app/scenes/CurrentTrackScreen/DailyFocusScreen/ActionSelected/CancelActionScreen";
import ReflexiveAnalysisScreen from "../scenes/FeedbackScreen/ReflexiveAnalysis/ReflexiveAnalysisScreen/ReflexiveAnalysisScreen";
import ActionFeelingScreen from "app/scenes/FeedbackScreen/ReflexiveAnalysis/ActionFeelingScreen/ActionFeelingScreen";
import ActionRatingScreen from "app/scenes/FeedbackScreen/ReflexiveAnalysis/ActionRatingScreen/ActionRatingScreen";
import RecapFeedbackScreen from "app/scenes/FeedbackScreen/ReflexiveAnalysis/RecapFeedbackScreen/RecapFeedbackScreen";
import SetupPicture from "app/scenes/Signup/ProfileSetup/SetupPicture/SetupPicture";
import SetupName from "app/scenes/Signup/ProfileSetup/SetupName";
import SignupCreateAccount from "app/scenes/Signup/ProfileSetup/SetupEmail";
import ReflexiveAnalysisTypeScreen from "app/scenes/FeedbackScreen/ReflexiveAnalysis/ReflexiveAnalysisTypeScreen";
import {
  isWebDesktop,
  isWebMobile,
} from "@microdoing/shared/utils/deviceDetectors";
import LoginScreen from "app/scenes/LoginScreen/LoginScreen";
import WelcomeScreen from "app/scenes/WelcomeScreen/WelcomeScreen";
import Signup from "app/scenes/Signup/Signup";
import ProfileSettings from "app/scenes/Settings/ProfileSettings";
import Survey from "app/scenes/Survey/Survey";
import theme from "app/Theme/Theme";
import TeamScreen from "app/scenes/CurrentTrackScreen/TeamScreen/TeamScreen";
import HistoricalList from "app/scenes/Historical/HistoricalList";
import ActionHistorical from "app/scenes/Historical/ActionHistorical";
import ReviewChatScreen from "app/scenes/MessageScreen/ReviewChatScreen";
import CertificateScreen from "app/scenes/CurrentTrackScreen/DailyFocusScreen/CertificateScreen";
import NewConversationScreen from "app/scenes/MessageScreen/NewConversationScreen";

const Stack = createNativeStackNavigator();
const TopTab = createMaterialTopTabNavigator();
const Tab = createBottomTabNavigator();
const UserProfileStack = createNativeStackNavigator();
const NotificationStack = createNativeStackNavigator();
const CoachingStack = createNativeStackNavigator();

export function ResourceTabs({
  hasCoachingResources = false,
  hasCampaignResources = false,
}) {
  return (
    <TopTab.Navigator
      lazy={true}
      tabBar={props => <TopTabBar {...props} />}
      initialRouteName="CampaignResources"
      screenOptions={{ headerShown: false, swipeEnabled: true }}
    >
      {hasCampaignResources && (
        <TopTab.Screen
          screenOptions={{ headerShown: false }}
          name="CampaignResources"
          component={CampaignResources}
          options={{ tabBarLabel: i18n.t("campaign-resources") }}
        />
      )}
      {hasCoachingResources && (
        <TopTab.Screen
          screenOptions={{ headerShown: false }}
          name="CoachResources"
          component={CoachResources}
          options={{ tabBarLabel: i18n.t("coach-resources") }}
        />
      )}
    </TopTab.Navigator>
  );
}

const getActiveRouteName = state => {
  const route = state.routes[state.index];

  if (route.state) {
    // Dive into nested navigators
    return getActiveRouteName(route.state);
  }

  return route.name;
};

function CoachingNavigator() {
  return (
    <CoachingStack.Navigator
      headerMode="none"
      screenOptions={{ headerShown: false }}
    >
      <CoachingStack.Screen
        name="CoachingScreen"
        component={Coaching}
        screenOptions={{ headerShown: false }}
      />
    </CoachingStack.Navigator>
  );
}

function UserProfileNavigator() {
  return (
    <UserProfileStack.Navigator
      headerMode="none"
      initialRouteName="UserProfile"
      screenOptions={{ headerShown: false }}
    >
      <UserProfileStack.Screen
        name="UserProfile"
        component={UserProfile}
        screenOptions={{ headerShown: false }}
      />
    </UserProfileStack.Navigator>
  );
}

function NotificationNavigator() {
  return (
    <NotificationStack.Navigator
      headerMode="none"
      initialRouteName="Notification"
      screenOptions={{ headerShown: false }}
    >
      <NotificationStack.Screen
        name="Notification"
        component={NotificationScreen}
        screenOptions={{ headerShown: false }}
      />
    </NotificationStack.Navigator>
  );
}

function NoTabStack() {
  return (
    <Stack.Group
      screenOptions={{
        headerShown: false,
        gestureResponseDistance: 250,
      }}
    >
      <Stack.Screen
        component={HistoricalList}
        key="HistoricalList"
        name="HistoricalList"
      />
      <Stack.Screen key="Library" name="Library" component={Library} />
      <Stack.Screen key="Survey" name="Survey" component={Survey} />
      <Stack.Screen key="Message" name="Message" component={MessageScreen} />
      <Stack.Screen
        key={"UserProfileSettings"}
        name="UserProfileSettings"
        component={ProfileSettings}
      />
      <Stack.Screen
        key="ExternalProfile"
        name="ExternalProfile"
        component={ExternalProfile}
      />
      <Stack.Screen
        key="Historical"
        name="Historical"
        component={HistoricalScreen}
      />
      <Stack.Screen
        key="Directory"
        name="Directory"
        component={DirectoryScreen}
      />
      <Stack.Screen
        key="Chat"
        name="Chat"
        initialParams={{ conversationIdGroup: "" }}
        component={ChatScreen}
      />
      <Stack.Screen
        key="NewConversation"
        name="NewConversation"
        screenOptions={{ presentation: "modal" }}
        initialParams={{ conversationIdGroup: "" }}
        component={NewConversationScreen}
      />
      <Stack.Screen key="Search" name="Search" component={AlgoliaSearch} />
      <Stack.Screen
        key="FeelingEvolution"
        name="FeelingEvolution"
        component={FeelingEvolution}
      />
      <Stack.Screen key="Poll" name="Poll" component={PollScreen} />
      <Stack.Screen key="Action" name="Action" component={ActionScreen} />
      <Stack.Screen
        key="HistoricalAction"
        name="HistoricalAction"
        component={ActionHistorical}
      />
      <Stack.Screen
        key="RandomCoffee"
        name="RandomCoffee"
        component={RandomCoffeeScreen}
      />
      <Stack.Screen
        key="TeamRanking"
        name="TeamRanking"
        component={RankingScreen}
      />
      <Stack.Screen key="Ranking" name="Ranking" component={RankingScreen} />
      <Stack.Screen
        key="UserRanking"
        name="UserRanking"
        component={UserRankingScreen}
      />
      <Stack.Screen name="Mbti" key="Mbti" component={MbtiSelectorScreen} />
      <Stack.Screen key="SeeTrack" name="SeeTrack" component={SeeTrackScreen} />
      <Stack.Screen key="Legal" name="Legal" component={LegalScreen} />
      <Stack.Screen
        key="LibraryFeedback"
        name="LibraryFeedback"
        component={LibraryFeedback}
      />
      <Stack.Screen
        key="SetReminders"
        name="SetReminders"
        component={SetRemindersScreen}
      />
      <Stack.Screen
        key="NewCampaignModal"
        name="NewCampaignModal"
        component={NewCampaignModal}
      />
      <Stack.Screen
        name="TeamDetails"
        key="TeamDetails"
        component={TeamDetailsScreen}
      />
      <Stack.Screen
        name="AllowNotifications"
        key="AllowNotifications"
        component={AllowNotificationsScreen}
      />
      <Stack.Screen
        name="ReflexiveAnalysisType"
        key="ReflexiveAnalysisType"
        component={ReflexiveAnalysisTypeScreen}
      />
      <Stack.Screen
        name="ReflexiveAnalysisScreen"
        key="ReflexiveAnalysisScreen"
        component={ReflexiveAnalysisScreen}
      />
      <Stack.Screen
        name="ActionFeelingScreen"
        key="ActionFeelingScreen"
        component={ActionFeelingScreen}
      />
      <Stack.Screen
        name="ActionRatingScreen"
        key="ActionRatingScreen"
        component={ActionRatingScreen}
      />
      <Stack.Screen
        name="RecapFeedbackScreen"
        key="RecapFeedbackScreen"
        component={RecapFeedbackScreen}
      />
      <Stack.Screen
        name="ReviewChatScreen"
        key="ReviewChatScreen"
        component={ReviewChatScreen}
      />
      <Stack.Screen name="Disc" key="Disc" component={DiscSelectorScreen} />
      {!isWebDesktop && (
        <Stack.Screen key="Result" name="Result" component={ResultScreen} />
      )}
      {!isWebDesktop && (
        <Stack.Screen
          key="NextAction"
          name="NextAction"
          component={NextActionScreen}
        />
      )}
      {!isWebDesktop && (
        <Stack.Screen
          component={SelectActionScreen}
          key="SelectAction"
          name="SelectAction"
        />
      )}
      <Stack.Screen
        name="WeeklyGoal"
        key="WeeklyGoal"
        component={WeeklyGoalScreen}
      />
      <Stack.Screen
        name="CancelAction"
        key="CancelAction"
        component={CancelActionScreen}
      />
      <Stack.Screen
        name="CertificateScreen"
        key="CertificateScreen"
        component={CertificateScreen}
      />
    </Stack.Group>
  );
}

function HomeTabMobile() {
  return (
    <Tab.Navigator
      screenOptions={{ headerShown: false }}
      initialRouteName="Home"
      headerMode="none"
      tabBar={props => <TabBar {...props} />}
    >
      <Tab.Group screenOptions={{ headerShown: false }}>
        <Tab.Screen
          name="Home"
          component={DailyFocusScreen}
          screenOptions={{ headerShown: false }}
        />
        <Tab.Screen
          name="Team"
          component={TeamScreen}
          screenOptions={{ headerShown: false }}
        />
        <Tab.Screen
          name="Progress"
          component={ProgressScreen}
          screenOptions={{ headerShown: false }}
        />
        <Tab.Screen
          name="Coaching"
          component={CoachingNavigator}
          screenOptions={{ headerShown: false }}
        />
        <Tab.Screen
          name="Notification"
          component={NotificationNavigator}
          screenOptions={{ headerShown: false }}
        />
        <Tab.Screen
          name="UserProfile"
          component={UserProfileNavigator}
          screenOptions={{ headerShown: false }}
        />
      </Tab.Group>
    </Tab.Navigator>
  );
}

const CampaignStack = createNativeStackNavigator();

function CampaignStackScreen() {
  return (
    <CampaignStack.Navigator
      screenOptions={{ headerShown: false }}
      initialRouteName="DailyFocusScreen"
      headerMode="none"
      options={{ tabBarLabel: i18n.t("daily-focus") }}
    >
      <CampaignStack.Screen
        name="DailyFocusScreen"
        key="DailyFocusScreen"
        component={DailyFocusScreen}
      />
      <CampaignStack.Screen
        component={SelectActionScreen}
        key="SelectAction"
        name="SelectAction"
      />
      <Stack.Screen key="Action" name="Action" component={ActionScreen} />
      <CampaignStack.Screen
        name="ReflexiveAnalysisType"
        key="ReflexiveAnalysisType"
        component={ReflexiveAnalysisTypeScreen}
      />
      <CampaignStack.Screen
        name="ReflexiveAnalysisScreen"
        key="ReflexiveAnalysisScreen"
        component={ReflexiveAnalysisScreen}
      />
      <CampaignStack.Screen
        name="RecapFeedbackScreen"
        key="RecapFeedbackScreen"
        component={RecapFeedbackScreen}
      />
      <CampaignStack.Screen
        name="ActionRatingScreen"
        key="ActionRatingScreen"
        component={ActionRatingScreen}
      />
      <CampaignStack.Screen
        name="ActionFeelingScreen"
        key="ActionFeelingScreen"
        component={ActionFeelingScreen}
      />
      <CampaignStack.Screen
        key="Result"
        name="Result"
        component={ResultScreen}
      />
      <CampaignStack.Screen
        key="NextAction"
        name="NextAction"
        component={NextActionScreen}
      />
      <CampaignStack.Screen
        name="CancelAction"
        key="CancelAction"
        component={CancelActionScreen}
      />
      <CampaignStack.Screen
        name="CertificateScreen"
        key="CertificateScreen"
        component={CertificateScreen}
      />
    </CampaignStack.Navigator>
  );
}

const ProgressionStack = createNativeStackNavigator();

function ProgressionStackScreen() {
  return (
    <ProgressionStack.Navigator
      screenOptions={{ headerShown: false }}
      initialRouteName="ProgressScreen"
      headerMode="none"
      options={{ tabBarLabel: i18n.t("progress") }}
    >
      <ProgressionStack.Screen
        name="ProgressScreen"
        key="ProgressScreen"
        component={ProgressScreen}
      />
      <ProfileStack.Screen
        component={HistoricalList}
        key="HistoricalList"
        name="HistoricalList"
      />
      <ProfileStack.Screen
        key="HistoricalAction"
        name="HistoricalAction"
        component={ActionHistorical}
      />
      <ProgressionStack.Screen
        key="UserRanking"
        name="UserRanking"
        component={UserRankingScreen}
      />
      <ProgressionStack.Screen
        name="ReviewChatScreen"
        key="ReviewChatScreen"
        component={ReviewChatScreen}
      />
    </ProgressionStack.Navigator>
  );
}

const TeamStack = createNativeStackNavigator();

function TeamStackScreen() {
  return (
    <TeamStack.Navigator
      screenOptions={{ headerShown: false }}
      initialRouteName="TeamScreen"
      headerMode="none"
      options={{ tabBarLabel: i18n.t("team") }}
    >
      <TeamStack.Screen
        name="TeamScreen"
        key="TeamScreen"
        component={TeamScreen}
      />
      <TeamStack.Screen
        key="TeamRanking"
        name="TeamRanking"
        component={RankingScreen}
      />
      <TeamStack.Screen
        key="TeamDetails"
        name="TeamDetails"
        component={TeamDetailsScreen}
      />
    </TeamStack.Navigator>
  );
}

const ProfileSettingsStack = createNativeStackNavigator();

function ProfileSettingsStackScreen() {
  return (
    <ProfileSettingsStack.Navigator
      screenOptions={{ headerShown: false }}
      initialRouteName="UserProfileSettings"
      headerMode="none"
      options={{ tabBarLabel: i18n.t("breadcrumb-profile-settings") }}
    >
      <ProfileSettingsStack.Screen
        key="UserProfileSettings"
        name="UserProfileSettings"
        component={ProfileSettings}
      />
      <ProfileSettingsStack.Screen
        name="Mbti"
        key="Mbti"
        component={MbtiSelectorScreen}
      />
      <ProfileSettingsStack.Screen
        name="Disc"
        key="Disc"
        component={DiscSelectorScreen}
      />
      <ProfileSettingsStack.Screen
        key="Legal"
        name="Legal"
        component={LegalScreen}
      />
    </ProfileSettingsStack.Navigator>
  );
}

const ProfileStack = createNativeStackNavigator();

function ProfileStackScreen() {
  return (
    <ProfileStack.Navigator
      screenOptions={{ headerShown: false }}
      initialRouteName="UserProfile"
      headerMode="none"
      options={{ tabBarLabel: i18n.t("breadcrumb-profile") }}
    >
      <ProfileStack.Screen
        name="UserProfile"
        component={UserProfile}
        screenOptions={{ headerShown: false }}
      />
      <ProfileStack.Screen
        name="CertificateScreen"
        key="CertificateScreen"
        component={CertificateScreen}
      />
    </ProfileStack.Navigator>
  );
}

function HomeTabDesktop() {
  return (
    <Tab.Navigator
      screenOptions={{ headerShown: false }}
      initialRouteName="DailyFocusScreen"
      headerMode="none"
      tabBar={props => <DesktopTabBar {...props} />}
    >
      <Tab.Group screenOptions={{ headerShown: false }}>
        <Tab.Screen
          screenOptions={{ headerShown: false }}
          name="DailyFocusScreen"
          component={CampaignStackScreen}
          options={{ tabBarLabel: i18n.t("daily-focus") }}
        />
        <Tab.Screen
          screenOptions={{ headerShown: false }}
          name="ProgressScreen"
          component={ProgressionStackScreen}
          options={{ tabBarLabel: i18n.t("progress") }}
        />
        <Tab.Screen
          screenOptions={{ headerShown: false }}
          name="TeamScreen"
          component={TeamStackScreen}
          options={{ tabBarLabel: i18n.t("team") }}
        />
        <Tab.Screen
          key="Message"
          name="Message"
          initialParams={{ conversationIdGroup: "" }}
          component={MessageScreen}
        />
        <Tab.Screen
          key="Notifications"
          name="Notifications"
          component={NotificationScreen}
        />
        <Tab.Screen
          name="UserProfile"
          component={ProfileStackScreen}
          screenOptions={{ headerShown: false }}
        />
        <Tab.Screen
          name="UserProfileSettings"
          component={ProfileSettingsStackScreen}
          screenOptions={{ headerShown: false }}
        />
        <Tab.Screen
          key="ExternalProfile"
          name="ExternalProfile"
          component={ExternalProfile}
          screenOptions={{ headerShown: false }}
        />
        <Tab.Screen
          key="SelectAction"
          name="SelectAction"
          component={SelectActionScreen}
        />
        <ProfileStack.Screen
          name="Mbti"
          key="Mbti"
          component={MbtiSelectorScreen}
        />
      </Tab.Group>
    </Tab.Navigator>
  );
}

function HomeStack() {
  return (
    <Stack.Navigator headerMode="none" screenOptions={{ headerShown: false }}>
      <Stack.Screen
        name="Main"
        component={isWebDesktop ? HomeTabDesktop : HomeTabMobile}
        screenOptions={{ headerShown: false }}
      />
      <Stack.Group screenOptions={{ headerShown: false }}>
        {NoTabStack()}
      </Stack.Group>
    </Stack.Navigator>
  );
}

function AuthStack() {
  return (
    <Stack.Navigator
      initialRouteName="Welcome"
      headerMode="none"
      options={{ headerShown: false }}
    >
      <Stack.Group screenOptions={{ headerShown: false }}>
        <Stack.Screen
          name="Welcome"
          component={WelcomeScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen name="Signup" component={Signup} />
        <Stack.Screen name="SignupSetupName" component={SetupName} />
        <Stack.Screen
          name="SignupCreateAccount"
          component={SignupCreateAccount}
        />
        <Stack.Screen name="SignupSetupPicture" component={SetupPicture} />
        <Stack.Screen name="SignupPhoneNumber" component={SignupPhoneNumber} />
        <Stack.Screen name="SignupMBTI" component={SignupMBTI} />
        <Stack.Screen name="Login" component={LoginScreen} />
        <Stack.Screen name="SignupSliders" component={SignupSliders} />
        <Stack.Screen name="PreferenceSignup" component={PreferenceSignup} />
        <Stack.Screen name="ForgotPassword" component={ForgotPasswordScreen} />
        <Stack.Screen name="SignupTopics" component={SignupTopics} />
        <Stack.Screen name="WeeklyGoal" component={WeeklyGoalScreen} />
        <Stack.Screen key="Survey" name="Survey" component={Survey} />
        <Stack.Screen name="Intro" component={IntroScreen} />
        <Stack.Screen
          key="SetReminders"
          name="SetReminders"
          component={SetRemindersScreen}
        />
        <Stack.Screen
          name="AllowNotifications"
          key="AllowNotifications"
          component={AllowNotificationsScreen}
        />
      </Stack.Group>
    </Stack.Navigator>
  );
}

const linking = {
  prefixes: [
    /* your linking prefixes */
  ],
  config: {
    screens: {
      Auth: {
        screens: {
          Welcome: "welcome",
          LoginScreen: "login",
          Signup: {
            path: "signup",
            screens: {
              Signup: "/:accessCode",
              SignupCreateAccount: "/email",
              SignupSetupName: "/name",
              SignupSetupPicture: "/picture",
              SignupPhoneNumber: "/phone",
              SignupMBTI: "/mbti",
              Intro: "/onboarding",
              Survey: "/onboarding/questions",
              SignupSliders: "/onboarding/questions-personnalize",
              PreferenceSignup: "/onboarding/category",
              SignupTopics: "/onboarding/topic",
              WeeklyGoal: "/goal",
              SetReminders: "/reminders",
              AllowNotifications: "/notification",
            },
          },
          ForgotPassword: "forget-password",
        },
      },
      Main: {
        screens: {
          ProgressScreen: "progress",
          Message: "message",
          Notifications: "notifications",
          TeamScreen: "team",
          UserProfile: {
            path: "me",
            screens: {
              UserProfile: "/",
              CertificateScreen: "certification",
            },
          },
          ExternalProfile: "profile",
          DailyFocusScreen: {
            path: "campaign",
            screens: {
              DailyFocusScreen: "/",
              ReflexiveAnalysisType: "analysis",
              ReflexiveAnalysisScreen: "analysis/content",
              ActionRatingScreen: "analysis/content/feeling/rating",
              ActionFeelingScreen: "analysis/content/feeling/",
              RecapFeedbackScreen: "analysis/content/feeling/rating/recap",
              ResultScreen: "analysis/result",
              NextActionScreen: "analysis/result/next",
              SelectAction: "select",
              CancelAction: "cancel",
              CertificateScreen: "certification",
            },
          },
        },
      },
    },

    /* configuration for matching screens with paths */
  },
};

const NavContainer = React.memo(() => {
  const routeNameRef = React.useRef();
  const isLoggedIn = useSelector(getIsLoggedIn);

  const dispatch = useDispatch();

  const setRouteFunction = routeName => dispatch(setRoute(routeName));

  const navTheme = {
    colors: {
      background: isLoggedIn
        ? theme.colors["neutral.75"]
        : theme.colors["neutral.white"],
    },
  };

  return (
    <NavigationContainer
      theme={navTheme}
      linking={linking}
      ref={navigationRef}
      onStateChange={async state => {
        const previousRouteName = routeNameRef.current;
        const currentRouteName = getActiveRouteName(state);

        if (previousRouteName !== currentRouteName) {
          if (Platform.OS !== "web") {
            await analytics().logScreenView({
              screen_name: currentRouteName,
              screen_class: currentRouteName,
            });
          }
        }
        routeNameRef.current = currentRouteName;
        setRouteFunction(currentRouteName);
      }}
    >
      <Stack.Navigator headerMode="none" options={{ headerShown: false }}>
        {isLoggedIn ? (
          <Stack.Screen
            name="Home"
            component={HomeStack}
            options={{ headerShown: false }}
          />
        ) : (
          <Stack.Screen
            name="Auth"
            component={AuthStack}
            options={{ headerShown: false }}
          />
        )}
      </Stack.Navigator>
    </NavigationContainer>
  );
});

const App = () => {
  return (
    <View style={styles.main}>
      <NavContainer />
    </View>
  );
};

const styles = {
  main: {
    flex: 1,
    position: "relative",
    backgroundColor: "transparent",
    with: "100%",
    height: isWebMobile ? "100vh" : "auto",
  },
};

export default App;
